import { broadcastQueryClient } from '@tanstack/query-broadcast-client-experimental';
import { QueryClient } from '@tanstack/react-query';
import { createRouter } from '@tanstack/react-router';
import { setupRouterSsrQueryIntegration } from '@tanstack/react-router-ssr-query';
import * as Sentry from '@sentry/tanstackstart-react';
import { routeTree } from './routeTree.gen';
import {
  getBrowserSentryTunnel,
  isBrowserSentryRuntimeEnabled,
} from '@/observability/client';

const getBrowserTracePropagationTargets = () => [
  'localhost',
  /^\/api\//,
  ...(typeof window === 'undefined' ? [] : [window.location.origin]),
];

export const getRouter = () => {
  const queryClient = new QueryClient();

  // Only enable cross-tab query sync in the browser.
  if (typeof window !== 'undefined') {
    broadcastQueryClient({
      queryClient,
      broadcastChannel: 'query-sync',
    });
  }

  const router = createRouter({
    routeTree,
    context: { queryClient },
    scrollRestoration: true,
    defaultPreloadStaleTime: 0,
    notFoundMode: 'root',
  });

  const sentryEnabled = isBrowserSentryRuntimeEnabled(import.meta.env);

  if (!router.isServer && sentryEnabled) {
    Sentry.init({
      dsn: import.meta.env.VITE_SENTRY_DSN,
      enabled: true,
      enableLogs: true,
      environment: import.meta.env.MODE,
      integrations: [
        // Send console.log, console.warn, and console.error calls as logs to Sentry
        Sentry.consoleLoggingIntegration({ levels: ['log', 'warn', 'error'] }),
        Sentry.replayIntegration(),
        Sentry.tanstackRouterBrowserTracingIntegration(router),
      ],
      replaysSessionSampleRate: 0.1,
      replaysOnErrorSampleRate: 1.0,
      sendDefaultPii: true,
      tunnel: getBrowserSentryTunnel(import.meta.env),
      tracePropagationTargets: getBrowserTracePropagationTargets(),
      tracesSampleRate: 1.0,
    });
  }

  setupRouterSsrQueryIntegration({
    router,
    queryClient,
  });

  return router;
};
